import { forwardRef, memo, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import DOMPurify from 'dompurify';
import { apiCall, ApiError } from '../utils/apiHelpers';
import { useRepoContext } from '../contexts/RepoContext';
import { useAuth } from '../contexts/AuthContext';
import { ExplainModal } from './ExplainModal';
import { AnnotationOverlay } from './AnnotationOverlay';

type FileContentType = 'md' | 'code' | 'gsheet' | 'gslide' | 'pdf' | 'text';

interface FileResponse {
    path: string;
    contentType: FileContentType;
    commitSha: string;
    title: string | null;
    body: string;
    html: string | null;
    sizeBytes: number;
    sourceUrl: string;
    sourceKind?: 'github' | 'gdrive';
    driveFileId?: string | null;
    driveMimeType?: string | null;
    pdfBase64?: string | null;
    pdfTooLarge?: boolean;
    lastIndexed: string;
}

interface SelectionRange {
    startLine: number;
    startCol: number;
    endLine: number;
    endCol: number;
}

interface Props {
    path: string | null;
    onFileLoaded: (info: { commitSha: string }) => void;
    onQuoteSelected: (quote: string, range: SelectionRange | null) => void;
    onSelectPath: (path: string) => void;
    onAnnotationSaved?: (info: { drawingId: string; previewDataUrl: string }) => void;
    navOpen: boolean;
    onToggleNav: () => void;
    asideOpen: boolean;
    onToggleAside: () => void;
}

/** Locate a quote within the file's raw body and return the line range it
 *  occupies. Tolerant of markdown↔HTML mismatches (list bullets, smart quotes,
 *  collapsed whitespace) — falls back to matching by the first and last
 *  distinctive 5-word phrases of the quote.
 */
function rangeFromQuote(body: string, quote: string): SelectionRange | null {
    if (!quote || !body) return null;
    const lines = body.split('\n');

    function lineOf(idx: number): number {
        if (idx < 0) return 1;
        let n = 0;
        for (let i = 0; i < idx; i++) if (body[i] === '\n') n++;
        return n + 1;
    }
    function colOf(idx: number): number {
        if (idx < 0) return 0;
        const lastNl = body.lastIndexOf('\n', idx - 1);
        return idx - (lastNl + 1);
    }
    function indexOfPhrase(phrase: string): number {
        if (!phrase) return -1;
        // Try literal first.
        let i = body.indexOf(phrase);
        if (i !== -1) return i;
        // Then loose: case-insensitive collapse-whitespace match.
        const norm = (s: string) => s.replace(/\s+/g, ' ').trim().toLowerCase();
        const nb = norm(body);
        const np = norm(phrase);
        if (!np) return -1;
        const ni = nb.indexOf(np);
        if (ni === -1) return -1;
        // Map the normalized offset back into the original body by counting
        // characters skipped to whitespace collapse. Approximate.
        let consumed = 0;
        let orig = 0;
        let inRun = false;
        while (consumed < ni && orig < body.length) {
            const ch = body[orig].toLowerCase();
            if (/\s/.test(ch)) {
                if (!inRun) { consumed++; inRun = true; }
            } else {
                consumed++;
                inRun = false;
            }
            orig++;
        }
        return orig;
    }
    function firstPhrase(s: string, words = 6): string {
        return (s.trim().split(/\s+/).slice(0, words).join(' '));
    }
    function lastPhrase(s: string, words = 6): string {
        const w = s.trim().split(/\s+/);
        return w.slice(Math.max(0, w.length - words)).join(' ');
    }

    // Try full quote first.
    let startIdx = indexOfPhrase(quote);
    let endIdx = startIdx === -1 ? -1 : startIdx + quote.length;
    if (startIdx === -1) {
        // Fall back to anchoring by the first-words and last-words phrases.
        const fp = firstPhrase(quote);
        const lp = lastPhrase(quote);
        startIdx = indexOfPhrase(fp);
        endIdx = indexOfPhrase(lp);
        if (startIdx === -1 || endIdx === -1) return null;
        endIdx += lp.length;
    }
    const startLine = lineOf(startIdx);
    const endLine = lineOf(Math.max(startIdx, endIdx - 1));
    const startCol = colOf(startIdx);
    const endCol = colOf(endIdx);
    // Sanity: clamp to file length
    if (startLine > lines.length || endLine > lines.length) return null;
    return { startLine, startCol, endLine, endCol };
}

/**
 * Resolve a relative href in a Markdown doc against the current doc's path.
 * `docs/auth/login.md` + `./other.md` → `docs/auth/other.md`
 * `docs/auth/login.md` + `../config.md` → `docs/config.md`
 * `docs/auth/login.md` + `/api.md` → `api.md`
 */
function resolveRelativeHref(fromPath: string, href: string): string {
    const cleanHref = href.split(/[#?]/)[0] ?? '';
    if (cleanHref === '') return fromPath;
    if (cleanHref.startsWith('/')) return cleanHref.replace(/^\/+/, '');
    const fromDir = fromPath.split('/').slice(0, -1);
    const parts = cleanHref.split('/');
    const stack = [...fromDir];
    for (const part of parts) {
        if (part === '' || part === '.') continue;
        if (part === '..') {
            stack.pop();
            continue;
        }
        stack.push(part);
    }
    return stack.join('/');
}


interface PageBodyHandle {
    getBodyElement: () => HTMLDivElement | null;
}

interface PageBodyProps {
    html: string | null;
    rawBody: string;
    contentType: FileContentType;
    /** Ref-stored callback for relative anchor clicks. Using a ref keeps
     *  PageBody memoized while the parent's handler is current. */
    onRelativeLinkRef: React.MutableRefObject<((href: string) => void) | null>;
    /** Current doc path — used to resolve relative hrefs at process time. */
    currentPath: string;
    /** Set of indexed paths in this repo. Links whose target doesn't resolve
     *  to one of these are marked non-clickable. */
    indexedPaths: Set<string>;
    /** PDF content (base64) — set only when contentType === 'pdf'. */
    pdfBase64: string | null;
    pdfTooLarge: boolean;
    pdfSourceUrl: string;
}

/**
 * The doc body. Rendered IMPERATIVELY via innerHTML inside a useEffect so React
 * does not touch the body DOM after the initial set. Wrapped in `memo` so it
 * only re-renders when the html/body string itself changes — every other
 * state change in the parent (selection rect, modal open, etc.) leaves the
 * body element completely untouched. This is what makes drag-selection stable.
 */
const PageBody = memo(forwardRef<PageBodyHandle, PageBodyProps>(function PageBody(
    { html, rawBody, contentType, onRelativeLinkRef, currentPath, indexedPaths, pdfBase64, pdfTooLarge, pdfSourceUrl },
    ref
) {
    const localRef = useRef<HTMLDivElement>(null);

    useImperativeHandle(
        ref,
        () => ({
            getBodyElement: () => localRef.current
        }),
        []
    );

    useEffect(() => {
        if (!localRef.current) return;
        // Embeddable HTML render path (markdown OR pre-sanitized Drive HTML
        // like CSV-rendered-as-table, plain-text-as-pre, etc.).
        if (html && (contentType === 'md' || contentType === 'gsheet' || contentType === 'gslide' || contentType === 'text')) {
            localRef.current.innerHTML = html;
            // Annotate every anchor based on whether it points to something we
            // can actually navigate to. External / mailto open in a new tab.
            // Relative links are checked against the indexed-paths set; if
            // they don't resolve, the link is rendered as inert text.
            for (const a of Array.from(localRef.current.querySelectorAll('a[href]'))) {
                const href = a.getAttribute('href') ?? '';
                if (/^[a-z]+:\/\//i.test(href) || href.startsWith('mailto:')) {
                    a.setAttribute('target', '_blank');
                    a.setAttribute('rel', 'noopener noreferrer');
                    continue;
                }
                if (href === '' || href.startsWith('#')) continue; // anchor
                const resolved = resolveRelativeHref(currentPath, href);
                const isFile = indexedPaths.has(resolved);
                const stripped = resolved.replace(/\/$/, '');
                const dirHasReadme =
                    !isFile && (
                        indexedPaths.has(`${stripped}/README.md`) ||
                        indexedPaths.has(`${stripped}/readme.md`) ||
                        indexedPaths.has(`${stripped}/Readme.md`) ||
                        indexedPaths.has(`${stripped}/index.md`)
                    );
                if (!isFile && !dirHasReadme) {
                    a.classList.add('broken-link');
                    a.setAttribute('aria-disabled', 'true');
                    a.setAttribute('tabindex', '-1');
                    a.setAttribute('title', `Not available: "${resolved}" is out of scope for this repo`);
                }
            }
        } else if (contentType === 'code') {
            const pre = document.createElement('pre');
            pre.className = 'page-body__code';
            const code = document.createElement('code');
            code.textContent = rawBody;
            pre.appendChild(code);
            localRef.current.replaceChildren(pre);
        } else if (contentType === 'pdf') {
            const container = document.createElement('div');
            container.className = 'page-body__pdf';
            if (pdfBase64) {
                const embed = document.createElement('embed');
                embed.setAttribute('type', 'application/pdf');
                embed.setAttribute('src', `data:application/pdf;base64,${pdfBase64}`);
                embed.style.width = '100%';
                embed.style.height = 'min(80vh, 1100px)';
                container.appendChild(embed);
                if (rawBody && rawBody.length > 0) {
                    const details = document.createElement('details');
                    details.className = 'page-body__pdf-text';
                    const summary = document.createElement('summary');
                    summary.textContent = 'Extracted text (for search & AI)';
                    details.appendChild(summary);
                    const pre = document.createElement('pre');
                    pre.textContent = rawBody;
                    details.appendChild(pre);
                    container.appendChild(details);
                }
            } else {
                const note = document.createElement('p');
                note.className = 'page-body__pdf-note';
                note.textContent = pdfTooLarge
                    ? 'This PDF is too large to render in-app. Open in Google Drive instead.'
                    : 'PDF content not available locally. Open in Google Drive instead.';
                container.appendChild(note);
                if (pdfSourceUrl) {
                    const a = document.createElement('a');
                    a.href = pdfSourceUrl;
                    a.target = '_blank';
                    a.rel = 'noopener noreferrer';
                    a.textContent = 'Open in Drive ↗';
                    container.appendChild(a);
                }
            }
            localRef.current.replaceChildren(container);
        } else {
            localRef.current.textContent = rawBody;
        }
    }, [html, rawBody, contentType, currentPath, indexedPaths, pdfBase64, pdfTooLarge, pdfSourceUrl]);

    // Event-delegated click on relative links. Stays attached once and reads
    // the latest handler from the ref, so PageBody stays memoized across
    // navigations.
    useEffect(() => {
        const bodyEl = localRef.current;
        if (!bodyEl) return;
        function onClick(e: MouseEvent) {
            if (e.defaultPrevented) return;
            const target = e.target as HTMLElement | null;
            const a = target?.closest && target.closest('a');
            if (!a) return;
            // Inert links: swallow the click. The visual styling already hints
            // these aren't navigable, but a stray click shouldn't 404 the user.
            if (a.classList.contains('broken-link')) {
                e.preventDefault();
                return;
            }
            const href = a.getAttribute('href');
            if (!href || href === '' || href.startsWith('#')) return;
            if (/^[a-z]+:\/\//i.test(href) || href.startsWith('mailto:')) return; // external — let it through
            e.preventDefault();
            onRelativeLinkRef.current?.(href);
        }
        bodyEl.addEventListener('click', onClick);
        return () => bodyEl.removeEventListener('click', onClick);
    }, [onRelativeLinkRef]);

    return <div ref={localRef} className="page-body" />;
}));

export function DocViewer({ path, onFileLoaded, onQuoteSelected, onSelectPath, onAnnotationSaved, navOpen, onToggleNav, asideOpen, onToggleAside }: Props) {
    const { tokenReady, userInfo } = useAuth();
    const { effectiveRepoId, allowedRepos } = useRepoContext();
    const lockdown = userInfo?.lockdown ?? null;
    const [doc, setDoc] = useState<FileResponse | null>(null);
    const [error, setError] = useState<string | null>(null);
    const bodyHandleRef = useRef<PageBodyHandle>(null);
    const pageRef = useRef<HTMLElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const [selectionRect, setSelectionRect] = useState<{ top: number; left: number; quote: string } | null>(null);
    const [explainQuote, setExplainQuote] = useState<string | null>(null);
    const [annotating, setAnnotating] = useState(false);
    const [indexedPaths, setIndexedPaths] = useState<Set<string>>(new Set());

    // Fetch the indexed-paths set for the current repo so internal links know
    // what's clickable. apiCall caches GETs for ~5s so this overlaps cheaply
    // with RepoNav's identical request.
    useEffect(() => {
        if (!tokenReady || !effectiveRepoId) return;
        let cancelled = false;
        (async () => {
            try {
                const [owner, name] = effectiveRepoId.split('/');
                const r = await apiCall<{ files: Array<{ path: string }> }>(
                    `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/tree`
                );
                if (!cancelled) setIndexedPaths(new Set(r.files.map((f) => f.path)));
            } catch {
                if (!cancelled) setIndexedPaths(new Set());
            }
        })();
        return () => { cancelled = true; };
    }, [tokenReady, effectiveRepoId]);
    /** Page max-width in pixels. 0 = "full" (no cap). */
    const [pageWidth, setPageWidth] = useState<number>(() => {
        const v = typeof window !== 'undefined' ? window.localStorage.getItem('docreview.pageWidthPx') : null;
        const n = v ? Number.parseInt(v, 10) : NaN;
        return Number.isFinite(n) && n >= 0 ? n : 900;
    });
    useEffect(() => {
        try { window.localStorage.setItem('docreview.pageWidthPx', String(pageWidth)); } catch {}
    }, [pageWidth]);
    function toggleFull() {
        setPageWidth((p) => (p === 0 ? 900 : 0));
    }
    const widthLabel = pageWidth === 0 ? 'Full' : `${pageWidth}px`;

    // Drag-resize handle. Mounted on the page's right edge.
    const dragStateRef = useRef<{ startX: number; startW: number } | null>(null);
    function onDragStart(e: React.PointerEvent<HTMLDivElement>) {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const w = pageRef.current?.offsetWidth ?? 900;
        dragStateRef.current = { startX: e.clientX, startW: w };
    }
    function onDragMove(e: React.PointerEvent<HTMLDivElement>) {
        const s = dragStateRef.current;
        if (!s) return;
        const dx = e.clientX - s.startX;
        // The handle grows the page from the right edge. Centered page means
        // both edges move outward — so dx should grow the width by 2× to keep
        // the handle under the cursor.
        const next = Math.max(560, Math.min(2200, s.startW + dx * 2));
        setPageWidth(next);
    }
    function onDragEnd(e: React.PointerEvent<HTMLDivElement>) {
        try { e.currentTarget.releasePointerCapture(e.pointerId); } catch {}
        dragStateRef.current = null;
    }
    function resetWidth() {
        setPageWidth(900);
    }

    // Ref-stored relative-link handler. PageBody attaches once and dereferences
    // this on every click, so the handler stays current without forcing
    // PageBody to re-render (which would re-set innerHTML and lose selection).
    const onRelativeLinkRef = useRef<((href: string) => void) | null>(null);
    onRelativeLinkRef.current = (href: string) => {
        if (!path) return;
        const resolved = resolveRelativeHref(path, href);
        onSelectPath(resolved);
    };


    // Scroll the reader back to the top whenever the path changes (clicking an
    // internal link, picking a different file, etc.). Otherwise the new doc
    // opens at whatever scroll position the previous one was at.
    useEffect(() => {
        if (scrollRef.current) scrollRef.current.scrollTop = 0;
    }, [path]);

    useEffect(() => {
        if (!tokenReady || allowedRepos === undefined || !effectiveRepoId || !path) {
            setDoc(null);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const [owner, name] = effectiveRepoId.split('/');
                const res = await apiCall<FileResponse>(
                    `/repos/${encodeURIComponent(owner)}/${encodeURIComponent(name)}/file?path=${encodeURIComponent(path)}`
                );
                if (!cancelled) {
                    setDoc(res);
                    setError(null);
                    onFileLoaded({ commitSha: res.commitSha });
                }
            } catch (err) {
                if (cancelled) return;
                // On 404, fall back to the repo's README so a stale URL or a
                // broken link doesn't leave the user staring at an error.
                if (err instanceof ApiError && err.status === 404 && indexedPaths.size > 0) {
                    const candidates = [
                        'README.md', 'readme.md', 'Readme.md', 'index.md', 'README.MD'
                    ];
                    // Find the shortest-path README anywhere in the repo.
                    let best: string | null = null;
                    for (const p of indexedPaths) {
                        const name = p.split('/').pop() ?? '';
                        if (candidates.includes(name)) {
                            if (!best || p.length < best.length) best = p;
                        }
                    }
                    if (best && best !== path) {
                        onSelectPath(best);
                        return;
                    }
                }
                setDoc(null);
                setError(err instanceof ApiError ? err.message : (err as Error).message);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [tokenReady, allowedRepos, effectiveRepoId, path, onFileLoaded]);

    /**
     * Selection capture — runs ONLY on mouseup so the body never re-renders
     * during the drag. The body is memoized + imperatively rendered, so
     * setSelectionRect calls from this handler also don't touch the body DOM.
     */
    useEffect(() => {
        function readSelection() {
            const bodyEl = bodyHandleRef.current?.getBodyElement();
            const pageEl = pageRef.current;
            if (!bodyEl || !pageEl) return;
            const sel = window.getSelection();
            if (!sel || sel.isCollapsed) return;
            const anchor = sel.anchorNode;
            const focus = sel.focusNode;
            if (!anchor || !bodyEl.contains(anchor.parentElement ?? anchor)) return;
            if (!focus || !bodyEl.contains(focus.parentElement ?? focus)) return;
            const text = sel.toString().trim();
            if (text.length === 0 || text.length > 2000) return;
            const range = sel.getRangeAt(0);
            const rect = range.getBoundingClientRect();
            const pageRect = pageEl.getBoundingClientRect();
            setSelectionRect({
                top: rect.bottom - pageRect.top + 6,
                left: rect.left - pageRect.left,
                quote: text
            });
        }

        function onMouseUp() {
            // Defer one tick so the browser finalizes the selection.
            window.setTimeout(readSelection, 0);
        }

        function onMouseDown(e: MouseEvent) {
            const target = e.target as Element | null;
            if (target && target.closest && target.closest('.selection-cta-group')) {
                // Clicking the CTA must not clear it before its onClick fires.
                return;
            }
            // Hide the CTA whenever the user clicks elsewhere. The browser
            // collapses the visual selection on mousedown anyway.
            setSelectionRect(null);
        }

        document.addEventListener('mouseup', onMouseUp);
        document.addEventListener('mousedown', onMouseDown);
        return () => {
            document.removeEventListener('mouseup', onMouseUp);
            document.removeEventListener('mousedown', onMouseDown);
        };
    }, []);

    // DRM listeners — activated when the tenant has lockdown.enabled = true.
    // Best-effort: blocks the in-browser vectors. OS screenshots, phone
    // photos, and extensions are not blockable from JS.
    useEffect(() => {
        if (!lockdown || !lockdown.enabled) return;
        function onCopy(e: ClipboardEvent) {
            if (lockdown?.disableCopy) {
                e.preventDefault();
                if (e.clipboardData) e.clipboardData.setData('text/plain', '');
            }
        }
        function onContext(e: MouseEvent) {
            if (lockdown?.disableContextMenu) e.preventDefault();
        }
        function onKey(e: KeyboardEvent) {
            const ctrl = e.ctrlKey || e.metaKey;
            const k = e.key.toLowerCase();
            // Allow keystrokes when focus is in an input/textarea so composers work.
            const t = e.target as HTMLElement | null;
            const inField = t && /^(input|textarea|select)$/i.test(t.tagName);
            if (lockdown?.disableSaveShortcut && ctrl && (k === 's' || k === 'p')) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            if (lockdown?.disableCopy && ctrl && !inField && (k === 'c' || k === 'x')) {
                e.preventDefault();
                e.stopPropagation();
                return;
            }
            if (lockdown?.disableDevToolsShortcut) {
                if (k === 'f12') { e.preventDefault(); return; }
                if (ctrl && e.shiftKey && (k === 'i' || k === 'j' || k === 'c')) {
                    e.preventDefault();
                    return;
                }
                if (ctrl && k === 'u') { e.preventDefault(); return; }
            }
        }
        function onBeforePrint() {
            if (lockdown?.disablePrint) document.body.classList.add('lockdown-print');
        }
        function onAfterPrint() {
            document.body.classList.remove('lockdown-print');
        }
        document.addEventListener('copy', onCopy);
        document.addEventListener('cut', onCopy);
        document.addEventListener('contextmenu', onContext);
        document.addEventListener('keydown', onKey, true);
        window.addEventListener('beforeprint', onBeforePrint);
        window.addEventListener('afterprint', onAfterPrint);
        return () => {
            document.removeEventListener('copy', onCopy);
            document.removeEventListener('cut', onCopy);
            document.removeEventListener('contextmenu', onContext);
            document.removeEventListener('keydown', onKey, true);
            window.removeEventListener('beforeprint', onBeforePrint);
            window.removeEventListener('afterprint', onAfterPrint);
        };
    }, [lockdown]);

    const safeHtml = useMemo(() => {
        if (!doc?.html) return null;
        if (doc.contentType === 'md') {
            return DOMPurify.sanitize(doc.html, {
                USE_PROFILES: { html: true },
                FORBID_TAGS: ['style', 'iframe', 'object', 'embed', 'form'],
                FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick']
            });
        }
        // Drive HTML (gsheet table / gslide / text) is sanitized server-side
        // already — sanitize a second time with table + pre tags allowed.
        if (doc.contentType === 'gsheet' || doc.contentType === 'gslide' || doc.contentType === 'text') {
            return DOMPurify.sanitize(doc.html, {
                USE_PROFILES: { html: true },
                FORBID_TAGS: ['style', 'iframe', 'object', 'embed', 'form'],
                FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick']
            });
        }
        return null;
    }, [doc?.contentType, doc?.html]);

    if (!path) {
        return (
            <div className="reader reader--empty">
                <p>Pick a file from the left to start reading.</p>
            </div>
        );
    }
    if (error) {
        return <div className="reader reader--error">{error}</div>;
    }
    if (!doc) {
        return <div className="reader reader--loading">Loading…</div>;
    }

    const watermarkText = lockdown?.enabled && lockdown.watermark
        ? `${userInfo?.email ?? ''} · ${new Date().toLocaleString()}`
        : null;

    return (
        <div className={'reader' + (lockdown?.enabled ? ' reader--lockdown' : '')}>
            <div className="reader-toolbar">
                <div className="reader-toolbar__row">
                    <div className="reader-toolbar__title">{doc.title ?? doc.path}</div>
                    <div className="reader-toolbar__actions">
                        <button
                            type="button"
                            className={'icon-btn' + (navOpen ? ' icon-btn--active' : '')}
                            onClick={onToggleNav}
                            aria-pressed={navOpen}
                            title={navOpen ? 'Hide files' : 'Show files'}
                        >
                            <span aria-hidden="true">📁</span>
                        </button>
                        <button
                            type="button"
                            className={'icon-btn' + (asideOpen ? ' icon-btn--active' : '')}
                            onClick={onToggleAside}
                            aria-pressed={asideOpen}
                            title={asideOpen ? 'Hide comments' : 'Show comments'}
                        >
                            <span aria-hidden="true">💬</span>
                        </button>
                        <span className="reader-toolbar__sep" aria-hidden="true" />
                        <span className="reader-toolbar__width" title="Drag the page edges to resize">
                            {widthLabel}
                        </span>
                        <button
                            type="button"
                            className={'icon-btn' + (pageWidth === 0 ? ' icon-btn--active' : '')}
                            onClick={toggleFull}
                            title={pageWidth === 0 ? 'Restore page width' : 'Fill the reader width'}
                        >
                            <span aria-hidden="true">⛶</span>
                        </button>
                        <button
                            type="button"
                            className="icon-btn"
                            onClick={resetWidth}
                            title="Reset page width to 900px"
                        >
                            <span aria-hidden="true">↺</span>
                        </button>
                        <span className="reader-toolbar__sep" aria-hidden="true" />
                        <button
                            type="button"
                            className={'icon-btn' + (annotating ? ' icon-btn--active' : '')}
                            onClick={() => setAnnotating((v) => !v)}
                            disabled={!doc.commitSha}
                            title={annotating ? 'Exit annotate mode' : 'Annotate (draw on the page)'}
                        >
                            <span aria-hidden="true">✎</span>
                        </button>
                    </div>
                </div>
                <div className="reader-toolbar__meta">
                    <code className="reader-toolbar__path">{doc.path}</code>
                    <a
                        className="reader-toolbar__source"
                        href={doc.sourceUrl}
                        target="_blank"
                        rel="noreferrer noopener"
                    >
                        {doc.sourceKind === 'gdrive'
                            ? 'View in Google Drive ↗'
                            : `GitHub @ ${doc.commitSha.slice(0, 7)} ↗`}
                    </a>
                </div>
            </div>
            <div ref={scrollRef} className="reader-scroll">
                <article
                    ref={pageRef}
                    className={'page' + (pageWidth === 0 ? ' page--full' : '')}
                    style={pageWidth === 0 ? undefined : { maxWidth: `${pageWidth}px` }}
                    data-content-type={doc.contentType}
                >
                    {watermarkText && (
                        <div className="page-watermark-tile" aria-hidden="true">
                            {Array.from({ length: 14 }).map((_, i) => (
                                <span key={i}>{watermarkText}</span>
                            ))}
                        </div>
                    )}
                    <div
                        className="page-resize-handle page-resize-handle--right"
                        onPointerDown={onDragStart}
                        onPointerMove={onDragMove}
                        onPointerUp={onDragEnd}
                        onPointerCancel={onDragEnd}
                        title="Drag to resize the page"
                        aria-label="Resize page"
                    />
                    <div
                        className="page-resize-handle page-resize-handle--left"
                        onPointerDown={onDragStart}
                        onPointerMove={onDragMove}
                        onPointerUp={onDragEnd}
                        onPointerCancel={onDragEnd}
                        aria-hidden="true"
                    />
                    <header className="page-header-block">
                        <h1>{doc.title ?? doc.path}</h1>
                        <div className="page-header-block__path">{doc.path}</div>
                    </header>
                    <PageBody
                        ref={bodyHandleRef}
                        html={safeHtml}
                        rawBody={doc.body}
                        contentType={doc.contentType}
                        onRelativeLinkRef={onRelativeLinkRef}
                        currentPath={path}
                        indexedPaths={indexedPaths}
                        pdfBase64={doc.pdfBase64 ?? null}
                        pdfTooLarge={doc.pdfTooLarge ?? false}
                        pdfSourceUrl={doc.sourceUrl}
                    />
                    {annotating && effectiveRepoId && path && doc.commitSha && (
                        <AnnotationOverlay
                            repoId={effectiveRepoId}
                            path={path}
                            commitSha={doc.commitSha}
                            targetRef={pageRef}
                            onCancel={() => setAnnotating(false)}
                            onSaved={(info) => {
                                setAnnotating(false);
                                onAnnotationSaved?.({
                                    drawingId: info.drawingId,
                                    previewDataUrl: info.previewDataUrl
                                });
                            }}
                        />
                    )}
                    {!annotating && selectionRect && (
                        <div
                            className="selection-cta-group"
                            style={{ top: selectionRect.top, left: selectionRect.left }}
                        >
                            <button
                                type="button"
                                className="selection-cta"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    const range = rangeFromQuote(doc.body, selectionRect.quote);
                                    onQuoteSelected(selectionRect.quote, range);
                                    window.getSelection()?.removeAllRanges();
                                    setSelectionRect(null);
                                }}
                            >
                                + Comment
                            </button>
                            <button
                                type="button"
                                className="selection-cta selection-cta--ai"
                                onMouseDown={(e) => e.preventDefault()}
                                onClick={() => {
                                    setExplainQuote(selectionRect.quote);
                                    window.getSelection()?.removeAllRanges();
                                    setSelectionRect(null);
                                }}
                            >
                                Explain (AI)
                            </button>
                        </div>
                    )}
                </article>
            </div>
            {explainQuote && effectiveRepoId && path && (
                <ExplainModal
                    repoId={effectiveRepoId}
                    path={path}
                    quote={explainQuote}
                    onClose={() => setExplainQuote(null)}
                />
            )}
        </div>
    );
}
